import React, { useEffect, useMemo, useState, Fragment } from 'react';
import { observer } from 'mobx-react';
import { find, map } from 'lodash';
import { useStores } from '../../hooks/use-stores';
import Question from './Question';
import AssessmentFooter from './AssessmentFooter';
import ThemedSpinner from '../../components/themed/ThemedSpinner';

function filterBlockValues(blockValues) {
    let result = {};
    map(blockValues, (v, k) => {
        if (v != null && k !== null && k !== 'undefined') result[k] = v;
    });
    return result;
}

function AssessmentQuestionPreview({
    companyId,
    question,
    isReview,
    questionsSelected,
    selectedQuestionId,
    setSelectedQuestionId,
    hasMoreQuestions,
    isMovingBack,
    setIsMovingBack,
    onRequestClose,
    loadingData,
    isWizard,
    hideFooter,
}) {
    const { authStore } = useStores();
    const { currentUser } = authStore;
    const [questionActions, setQuestionActions] = useState(null);
    const [questionId, setQuestionId] = useState(null);
    const [blockValues, setBlockValues] = useState({});
    const [completedBlocks, setCompletedBlocks] = useState([]);
    const [blockTransition, setBlockTransition] = useState(false);
    const [blockFadeIn, setBlockFadeIn] = useState(false);
    const [blockFadeOut, setBlockFadeOut] = useState(false);
    const [transitionTimer, setTransitionTimer] = useState(null);
    const [blockNum, setBlockNum] = useState(0);

    useEffect(() => {
        (async () => {
            if (blockTransition) {
                await onNext(null, true);
            }
            setBlockFadeIn(true);
            setBlockFadeOut(false);
        })();
    }, [blockTransition]);

    useEffect(() => {
        void blockFadeIn;
    }, [blockFadeIn]);

    useEffect(() => {
        void blockFadeOut;
    }, [blockFadeOut]);

    const ui = useMemo(() => {
        if (!question) return null;
        if (question.definition) return question.definition.ui;
        if (question.ui) return question.ui;
    }, [question]);

    const blockList = useMemo(
        () => (ui && ui.blocks ? ui.blocks : []),
        [ui]
    );

    useEffect(() => {
        if (!ui?.blocks) {
            setCompletedBlocks([]);
            return;
        }
        if (blockValues && Object.keys(blockValues).length > 0) {
            let filteredValues = filterBlockValues(blockValues);

            let completedBlocks = ui.blocks.map((block) => {
                let actionId = block.actionId;
                let x = filteredValues[actionId];

                if (Array.isArray(x) || typeof x === 'object') {
                    if (block.type === 'yes-no-list') {
                        if (
                            typeof x === 'object' &&
                            Object.keys(x).length === block.questions.length
                        ) {
                            return true;
                        }
                    } else if (x.length > 0) {
                        return true;
                    }
                } else if (typeof x === 'string') {
                    return true;
                }
                return false;
            });
            setCompletedBlocks(completedBlocks);
        } else {
            setCompletedBlocks([]);
        }
    }, [blockValues, questionId, ui]);

    useEffect(() => {
        loadData();
        const u = question?.definition?.ui || question?.ui;
        const len = u?.blocks?.length ?? 0;
        setBlockNum(isMovingBack && len > 0 ? len - 1 : 0);
        setBlockValues({});
        setQuestionActions(null);
    }, [question, isMovingBack]);

    useEffect(() => {
        (async () => {
            if (companyId && questionId) {
                let _questionActions = [];
                let _blockValues = {};
                let _blockNum = 0;
                _questionActions = [];
                _blockValues = {};
                const len =
                    question?.definition?.ui?.blocks?.length ??
                    question?.ui?.blocks?.length ??
                    0;
                setBlockNum(
                    isMovingBack && len > 0 ? len - 1 : _blockNum
                );
                setBlockValues(filterBlockValues(_blockValues));
                setQuestionActions(_questionActions);
            }
        })();
    }, [questionId, companyId]);

    const handleBlockChange = (
        value,
        actionId,
        block,
        omitTransition = false
    ) => {
        if (!actionId && !blockList[blockNum]) return;
        let blockActionId = actionId || blockList[blockNum].actionId;
        let updatedBlockValues = { ...(blockValues || {}) };
        let newVal = { ...updatedBlockValues, ...{ [blockActionId]: value } };

        setBlockValues(filterBlockValues(newVal));

        if (
            !omitTransition &&
            blockList.length > 1 &&
            blockNum < blockList.length - 1
        ) {
            setBlockFadeIn(false);
            setBlockFadeOut(true);
            if (transitionTimer) {
                try {
                    clearTimeout(transitionTimer);
                } catch (e) {
                    void e;
                }
            }
            setTransitionTimer(
                setTimeout(() => {
                    setTransitionTimer(null);
                    setBlockTransition(false);
                    setBlockTransition(true);
                }, 500)
            );
        }
    };

    const loadData = async () => {
        setQuestionId(question.id);
    };

    const onQuestionAction = async (questionId, action, value) => {
        //Keeping questionActions up-to-date, but maybe we don't need this...just POST every action
        let existingQuestionValue = find(questionActions, {
            questionId,
            action,
        });
        if (existingQuestionValue) {
            existingQuestionValue.value = value;
            setQuestionActions([...questionActions]);
        } else {
            existingQuestionValue = { questionId, action, value };
            setQuestionActions([...questionActions, existingQuestionValue]);
        }
    };

    const onNextBlock = async () => {
        let newBlockNum = Math.min(blockNum + 1, blockList.length - 1);
        setBlockNum(newBlockNum);
        let block = blockList[newBlockNum];
        let blockActionId = block.actionId;
        handleBlockChange(
            blockValues[blockActionId],
            blockActionId,
            block,
            true
        );
    };

    const onPrevBlock = async () => {
        let newBlockNum = Math.max(blockNum - 1, 0);
        setBlockNum(newBlockNum);
        let block = blockList[newBlockNum];
        let blockActionId = block.actionId;
        handleBlockChange(
            blockValues[blockActionId],
            blockActionId,
            block,
            true
        );
    };

    const onPreviousLesson = async (e, startAtLastBlock = false) => {
        setIsMovingBack(startAtLastBlock);
        questionsSelected.indexOf(selectedQuestionId) > 0 &&
            setSelectedQuestionId(
                questionsSelected[
                    questionsSelected.indexOf(selectedQuestionId) - 1
                ]
            );
    };

    const onNextLesson = async () => {
        setIsMovingBack(false);
        questionsSelected.indexOf(selectedQuestionId) !==
            questionsSelected.length - 1 &&
            setSelectedQuestionId(
                questionsSelected[
                    questionsSelected.indexOf(selectedQuestionId) + 1
                ]
            );
    };

    const onNext = async () => {
        if (!question) return;
        if (hasNextBlock()) return onNextBlock();
        return onNextLesson();
    };

    const onPrevious = async () => {
        if (!question) return;
        if (hasPreviousBlock()) return onPrevBlock();
        return onPreviousLesson(null, true);
    };

    const hasNextBlock = function () {
        return (
            blockList &&
            blockNum < blockList.length - 1 &&
            blockList[blockNum]
        );
    };

    const hasPreviousBlock = function () {
        return blockNum !== 0;
    };

    return (
        <Fragment>
            <div
                className={`modalContent ${
                    loadingData ? 'loading-centered' : ''
                }`}
            >
                {loadingData ? (
                    <ThemedSpinner />
                ) : !ui?.blocks ? (
                    <ThemedSpinner />
                ) : (
                    <Question
                        handleBlockChange={handleBlockChange}
                        question={question}
                        blockNum={blockNum}
                        onPrevBlock={onPrevBlock}
                        onNextBlock={onNextBlock}
                        blockValues={blockValues}
                        completedBlocks={completedBlocks}
                        userName={currentUser?.name || ''}
                        userEmail={currentUser?.email || ''}
                        onQuestionAction={onQuestionAction}
                        visibleBlocks={ui.blocks}
                        isReview={isReview}
                    />
                )}
            </div>
            {!hideFooter && (
                <AssessmentFooter
                    hasMoreQuestions={hasMoreQuestions}
                    onRequestClose={onRequestClose}
                    questionIndex={questionsSelected.indexOf(question.id)}
                    onPreviousLesson={onPreviousLesson}
                    blockNum={blockNum}
                    onPrevBlock={onPrevious}
                    visibleBlocks={ui.blocks}
                    hasPreviousBlock={hasPreviousBlock()}
                    hasNextBlock={hasNextBlock()}
                    onNextBlock={onNext}
                    questionCount={questionsSelected.length}
                    onNextLesson={onNextLesson}
                    isWizard={isWizard}
                />
            )}
        </Fragment>
    );
}

export default observer(AssessmentQuestionPreview);
